import { FilespaceMigrationStatus } from '@lucidlink/dto/validation';
import { ClassicFilespacesEligibleForMigrationResponseDto, ClassicMigrationDomainResponseDto, ClassicMigrationFilespaceDto, ClassicMigrationFilespacesResponseDto, ClassicMigrationFilespaceUsersResponseDto, FilespaceMigrationDto, FilespaceMigrationsResponseDto, MigrationFilespaceDto, WorkspaceResponseDto, } from '@lucidlink/dto/web-service-api';
import { parserFromDto } from '#fetcher/parse-utils';
import { AccountCrypto } from '#sdk/common/index';
import { prepareWorkspace } from '#sdk/workspaces-management/create-workspace/prepare-workspace';
import { isGuestUserAccountDetails } from '#utils/account-details';
import { RequiredCryptoError } from '../workspace-client/crypto';
export const createGeneralFilespaceMigrationsClient = ({ getGlobalFetcher, getClassicApiContext, getSessionManager, }) => {
    return new GeneralFilespaceMigrationsClientImpl(getGlobalFetcher, getClassicApiContext, getSessionManager);
};
export class GeneralFilespaceMigrationsClientImpl {
    getGlobalFetcher;
    getClassicApiContext;
    getSessionManager;
    constructor(getGlobalFetcher, getClassicApiContext, getSessionManager) {
        this.getGlobalFetcher = getGlobalFetcher;
        this.getClassicApiContext = getClassicApiContext;
        this.getSessionManager = getSessionManager;
    }
    async getMigrations(options = {}) {
        const { migrations } = await this.getGlobalFetcher().get(['filespace-migrations'], {
            parser: parserFromDto(FilespaceMigrationsResponseDto),
            queryParams: {
                destinationWorkspaceId: options.destinationWorkspaceId,
            },
        });
        return migrations.map((migration) => new SingleFilespaceMigrationClientImpl(this.getGlobalFetcher, migration));
    }
    async getMigration(id) {
        const migration = await this.getGlobalFetcher().get(['filespace-migrations', id], {
            parser: parserFromDto(FilespaceMigrationDto),
        });
        return new SingleFilespaceMigrationClientImpl(this.getGlobalFetcher, migration);
    }
    getMigrationFilespace(filespaceId) {
        return this.getGlobalFetcher().get(['filespace-migrations', 'filespaces', filespaceId], {
            parser: parserFromDto(MigrationFilespaceDto),
        });
    }
    async getClassicMigrationFilespaces() {
        return this.getGlobalFetcher().get(['filespace-migrations', 'classic', 'filespaces'], {
            parser: parserFromDto(ClassicMigrationFilespacesResponseDto),
            headers: {
                ...(await getClassicApiContextHeaders(this.getClassicApiContext)),
            },
        });
    }
    async getClassicFilespacesEligibleForMigration() {
        return this.getGlobalFetcher().get([
            'filespace-migrations',
            'classic',
            'filespaces',
            'eligible-for-migration',
        ], {
            parser: parserFromDto(ClassicFilespacesEligibleForMigrationResponseDto),
            headers: {
                ...(await getClassicApiContextHeaders(this.getClassicApiContext)),
            },
        });
    }
    async getClassicMigrationFilespace(filespaceId) {
        return this.getGlobalFetcher().get(['filespace-migrations', 'classic', 'filespaces', filespaceId], {
            parser: parserFromDto(ClassicMigrationFilespaceDto),
            headers: {
                ...(await getClassicApiContextHeaders(this.getClassicApiContext)),
            },
        });
    }
    async getClassicMigrationFilespaceUsers(filespaceId) {
        return this.getGlobalFetcher().get([
            'filespace-migrations',
            'classic',
            'filespaces',
            filespaceId,
            'users',
        ], {
            parser: parserFromDto(ClassicMigrationFilespaceUsersResponseDto),
            headers: {
                ...(await getClassicApiContextHeaders(this.getClassicApiContext)),
            },
        });
    }
    async getClassicMigrationDomain() {
        return this.getGlobalFetcher().get(['filespace-migrations', 'classic', 'domain'], {
            parser: parserFromDto(ClassicMigrationDomainResponseDto),
            headers: {
                ...(await getClassicApiContextHeaders(this.getClassicApiContext)),
            },
        });
    }
    async isMigrationWorkspaceNameAvailable(name) {
        const { isAvailable } = await this.getGlobalFetcher().get(['filespace-migrations', 'workspaces', 'availability'], {
            queryParams: { name },
        });
        return isAvailable;
    }
    async createWorkspaceForMigration({ name, }) {
        const accountDetails = await this.getSessionManager().getAccountDetails();
        if (isGuestUserAccountDetails(accountDetails)) {
            throw new RequiredCryptoError();
        }
        const accountCrypto = new AccountCrypto(accountDetails);
        const createWorkspaceDto = await prepareWorkspace({ name }, await accountCrypto.getAccountKey());
        return this.getGlobalFetcher().post(['filespace-migrations', 'workspaces'], {
            body: createWorkspaceDto,
            parser: parserFromDto(WorkspaceResponseDto),
            headers: {
                ...(await getClassicApiContextHeaders(this.getClassicApiContext)),
            },
        });
    }
    async getWorkspaceAssociatedWithClassicDomain(classicDomainId) {
        return this.getGlobalFetcher().get(['filespace-migrations', 'workspaces'], {
            queryParams: { classicDomainId },
            parser: parserFromDto(WorkspaceResponseDto),
            headers: {
                ...(await getClassicApiContextHeaders(this.getClassicApiContext)),
            },
        });
    }
}
export class SingleFilespaceMigrationClientImpl {
    getGlobalFetcher;
    migrationDto;
    constructor(getGlobalFetcher, migrationDto) {
        this.getGlobalFetcher = getGlobalFetcher;
        this.migrationDto = migrationDto;
    }
    get migration() {
        return structuredClone(this.migrationDto);
    }
    get canStart() {
        return (this.migrationDto.status ===
            FilespaceMigrationStatus.READY_FOR_MIGRATION);
    }
    get canComplete() {
        return this.migrationDto.status === FilespaceMigrationStatus.MIGRATING;
    }
    get canCancel() {
        return this.migrationDto.canCancel;
    }
    async cancel() {
        const updatedMigration = await this.getGlobalFetcher().put([
            'filespace-migrations',
            this.migrationDto.id,
            'transitions',
            'cancelled',
        ], {
            parser: parserFromDto(FilespaceMigrationDto),
        });
        this.migrationDto = updatedMigration;
    }
    async start() {
        const updatedMigration = await this.getGlobalFetcher().put([
            'filespace-migrations',
            this.migrationDto.id,
            'transitions',
            'migrating',
        ], {
            parser: parserFromDto(FilespaceMigrationDto),
        });
        this.migrationDto = updatedMigration;
    }
    async complete() {
        const updatedMigration = await this.getGlobalFetcher().put([
            'filespace-migrations',
            this.migrationDto.id,
            'transitions',
            'migrated',
        ], {
            parser: parserFromDto(FilespaceMigrationDto),
        });
        this.migrationDto = updatedMigration;
    }
    waitForReadyForMigration(options = {
        maxAttempts: 40,
        delayMs: 15 * 1000,
    }) {
        return this.waitForStatus(FilespaceMigrationStatus.READY_FOR_MIGRATION, [FilespaceMigrationStatus.IDLE, FilespaceMigrationStatus.PREPARING], options);
    }
    async waitForStatus(status, validPreviousStatuses, options) {
        let attempt = 0;
        while (attempt <= options.maxAttempts) {
            if (this.migrationDto.status === status) {
                return;
            }
            if (!validPreviousStatuses.includes(this.migrationDto.status)) {
                throw new Error(`The migration has ${this.migrationDto.status} and won't become ${status}`);
            }
            if (attempt === options.maxAttempts) {
                break;
            }
            await new Promise((resolve) => setTimeout(resolve, options.delayMs));
            this.migrationDto = await this.getGlobalFetcher().get(['filespace-migrations', this.migrationDto.id], {
                parser: parserFromDto(FilespaceMigrationDto),
            });
            attempt++;
        }
        throw new Error(`The migration failed to become ${status} within the given time`);
    }
}
export const getClassicApiContextHeaders = async (getClassicApiContext) => {
    const classicApiContext = await getClassicApiContext();
    if (!classicApiContext) {
        throw new Error('Either classic API token or partner organization ID must be provided for migration operations');
    }
    if ('token' in classicApiContext) {
        return {
            'X-Classic-Token': classicApiContext.token,
        };
    }
    return {
        'X-Partner-Organization-Id': classicApiContext.partnerOrganizationId,
    };
};
